// Leader Board template: turns an arbitrary JSON response into ranked rows.
//
// A mapping says where the rows are and which fields mean what:
//   { rowsPath, nameField, scoreField, rankField, format, order, extraFields }
// `detectMapping` guesses it from a sample response, so a typical
// `{ entries: [{ rank, nickname, duration_ms, build }] }` works with no setup.

const ROW_KEYS = ['entries', 'data', 'items', 'results', 'leaderboard', 'rows', 'players', 'scores'];
const NAME_KEYS = ['nickname', 'name', 'username', 'player_name', 'display_name', 'player', 'user'];
const SCORE_KEYS = ['score', 'points', 'duration_ms', 'time_ms', 'elapsed_ms', 'time', 'duration', 'value'];
const RANK_KEYS = ['rank', 'position', 'place'];

export const FORMATS = [
  { id: 'ms', label: 'Time (milliseconds)' },
  { id: 's', label: 'Time (seconds)' },
  { id: 'number', label: 'Number' },
];

const isObj = (v) => v && typeof v === 'object' && !Array.isArray(v);
const isScalar = (v) => ['string', 'number', 'boolean'].includes(typeof v);
const isIdKey = (k) => k === 'id' || k === '_id' || /(_id|Id|uuid|guid)$/i.test(k);
const pick = (preferred, keys) => preferred.find((k) => keys.includes(k)) || '';

function getPath(obj, path) {
  return path ? path.split('.').reduce((o, k) => o?.[k], obj) : obj;
}

// Paths of arrays of objects inside the response (one level deep), root first.
export function arrayPaths(json) {
  if (Array.isArray(json)) return [''];
  if (!isObj(json)) return [];
  const keys = Object.keys(json).filter((k) => Array.isArray(json[k]) && json[k].some(isObj));
  return [...ROW_KEYS.filter((k) => keys.includes(k)), ...keys.filter((k) => !ROW_KEYS.includes(k))];
}

export function sampleKeys(json, rowsPath) {
  const rows = getPath(json, rowsPath);
  const sample = Array.isArray(rows) ? rows.find(isObj) : null;
  return sample ? Object.keys(sample).filter((k) => isScalar(sample[k])) : [];
}

function guessFormat(field) {
  if (/(_ms|ms|millis|milliseconds)$/i.test(field)) return 'ms';
  if (/(_s|_sec|secs|seconds)$/i.test(field)) return 's';
  if (/time|duration/i.test(field)) return 'ms';
  return 'number';
}

// Fields shown as extra columns: everything scalar that isn't already mapped or an id.
function extraFieldsFor(keys, m) {
  return keys.filter((k) => ![m.nameField, m.scoreField, m.rankField].includes(k) && !isIdKey(k));
}

export function detectMapping(json, rowsPath = arrayPaths(json)[0] ?? '') {
  const rows = getPath(json, rowsPath);
  const sample = (Array.isArray(rows) ? rows.find(isObj) : null) || {};
  const keys = Object.keys(sample).filter((k) => isScalar(sample[k]));
  const numeric = keys.filter((k) => typeof sample[k] === 'number');

  const rankField = pick(RANK_KEYS, numeric);
  const nameField = pick(NAME_KEYS, keys)
    || keys.find((k) => typeof sample[k] === 'string' && !isIdKey(k)) || '';
  const scoreField = pick(SCORE_KEYS, numeric)
    || numeric.find((k) => k !== rankField && !isIdKey(k)) || '';
  const format = guessFormat(scoreField);

  // With an explicit rank the source already tells us which direction wins.
  let order = format === 'number' ? 'desc' : 'asc';
  if (rankField && scoreField && Array.isArray(rows) && rows.length > 1) {
    const ranked = rows.filter(isObj).sort((a, b) => a[rankField] - b[rankField]);
    const first = ranked[0][scoreField];
    const last = ranked[ranked.length - 1][scoreField];
    if (first !== last) order = first < last ? 'asc' : 'desc';
  }

  const mapping = { rowsPath, nameField, scoreField, rankField, format, order };
  return { ...mapping, extraFields: extraFieldsFor(keys, mapping) };
}

// Re-derive extra columns after the user changes a field in the mapping.
export function withExtraFields(json, mapping) {
  return { ...mapping, extraFields: extraFieldsFor(sampleKeys(json, mapping.rowsPath), mapping) };
}

const median = (sorted) => {
  if (!sorted.length) return null;
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

export function buildBoard(json, mapping) {
  const m = { ...detectMapping(json, mapping?.rowsPath), ...mapping };
  const raw = getPath(json, m.rowsPath);
  if (!Array.isArray(raw)) return { error: 'No list of rows found in the response', rows: [] };

  const asc = m.order === 'asc';
  let rows = raw
    .filter(isObj)
    .map((r) => ({
      name: String(r[m.nameField] ?? '—'),
      score: Number(r[m.scoreField]),
      rank: m.rankField ? Number(r[m.rankField]) : null,
      extras: Object.fromEntries((m.extraFields || []).map((k) => [k, r[k]])),
    }))
    .filter((r) => Number.isFinite(r.score));

  rows.sort((a, b) => (m.rankField && Number.isFinite(a.rank) && Number.isFinite(b.rank)
    ? a.rank - b.rank
    : asc ? a.score - b.score : b.score - a.score));
  rows = rows.map((r, i) => ({ ...r, rank: Number.isFinite(r.rank) ? r.rank : i + 1 }));

  const scores = rows.map((r) => r.score).sort((a, b) => a - b);
  const best = asc ? scores[0] : scores[scores.length - 1];
  const worst = asc ? scores[scores.length - 1] : scores[0];

  rows = rows.map((r) => ({
    ...r,
    gap: Math.abs(r.score - best),
    // Share of the leader's result: 100% for #1, lower for everyone behind.
    relative: !best || !r.score ? 0 : asc ? best / r.score : r.score / best,
  }));

  // Low-cardinality extra fields (build, platform, region…) get a breakdown.
  const breakdowns = (m.extraFields || [])
    .map((field) => {
      const counts = new Map();
      rows.forEach((r) => {
        const v = r.extras[field];
        if (v !== undefined && v !== null && v !== '') counts.set(String(v), (counts.get(String(v)) || 0) + 1);
      });
      return { field, values: [...counts.entries()].sort((a, b) => b[1] - a[1]) };
    })
    .filter((b) => b.values.length > 1 && b.values.length <= 12);

  return {
    mapping: m,
    rows,
    stats: rows.length ? {
      count: rows.length,
      best,
      leader: rows[0].name,
      median: median(scores),
      mean: scores.reduce((s, v) => s + v, 0) / scores.length,
      spread: Math.abs(worst - best),
    } : null,
    breakdowns,
  };
}

function formatDuration(ms) {
  const total = Math.round(ms / 10); // centiseconds
  const cs = total % 100;
  const s = Math.floor(total / 100) % 60;
  const m = Math.floor(total / 6000) % 60;
  const h = Math.floor(total / 360000);
  const pad = (n) => String(n).padStart(2, '0');
  return h ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}.${pad(cs)}`;
}

export function formatScore(value, format) {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  if (format === 'ms') return formatDuration(value);
  if (format === 's') return formatDuration(value * 1000);
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}
